import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Features = () => {
  const sectionRef = useRef();
  const featuresRef = useRef([]);

  const features = [
    {
      icon: "⚡",
      title: "Performance",
      description: "Engineered for athletes who demand excellence in every step."
    },
    {
      icon: "♻️",
      title: "Innovation",
      description: "Sustainable materials meet cutting-edge technology."
    },
    {
      icon: "✨",
      title: "Design",
      description: "Iconic style that transcends sport and culture."
    },
    {
      icon: "🌟",
      title: "Community",
      description: "Join millions who choose to Just Do It every day."
    }
  ];

  useEffect(() => {
    featuresRef.current.forEach((feature, index) => {
      gsap.fromTo(feature,
        { y: 80, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: index * 0.2,
          scrollTrigger: {
            trigger: feature,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });
  }, []);

  return (
    <Section ref={sectionRef}>
      <Container>
        <SectionHeader>
          <SectionTitle>Why Choose Nike</SectionTitle>
          <SectionSubtitle>Four pillars that define our commitment to excellence</SectionSubtitle>
        </SectionHeader>
        <FeaturesContainer>
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              ref={el => featuresRef.current[index] = el}
            >
              <FeatureIconWrapper>
                <FeatureIcon>{feature.icon}</FeatureIcon>
              </FeatureIconWrapper>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureItem>
          ))}
        </FeaturesContainer>
      </Container>
    </Section>
  );
};

export default Features;

const Section = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e0e0e0, transparent);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Anton', sans-serif;
  font-size: clamp(2.2rem, 4vw, 3.2rem);
  color: #111;
  margin-bottom: 16px;
  letter-spacing: 1px;
  font-weight: 400;
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: #666;
  font-weight: 300;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const FeaturesContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 24px;
  
  @media (max-width: 1200px) {
    gap: 20px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const FeatureItem = styled.div`
  flex: 1;
  background: #ffffff;
  border-radius: 12px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  min-height: 280px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #111, #666);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  @media (max-width: 1200px) {
    padding: 28px 20px;
    min-height: 260px;
  }
  
  @media (max-width: 768px) {
    flex-direction: row;
    text-align: left;
    min-height: auto;
    padding: 24px;
    align-items: center;
  }
`;

const FeatureIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #f8f9fa, #ffffff);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  ${FeatureItem}:hover & {
    background: linear-gradient(135deg, #111, #333);
    transform: scale(1.05);
  }
  
  @media (max-width: 1200px) {
    width: 56px;
    height: 56px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    margin-bottom: 0;
    margin-right: 20px;
  }
`;

const FeatureIcon = styled.div`
  font-size: 1.8rem;
  transition: all 0.3s ease;
  
  ${FeatureItem}:hover & {
    transform: scale(1.1);
    filter: brightness(1.2);
  }
  
  @media (max-width: 1200px) {
    font-size: 1.6rem;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #111;
  letter-spacing: 0.3px;
  
  @media (max-width: 1200px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
  
  @media (max-width: 768px) {
    font-size: 19px;
    margin-bottom: 8px;
  }
`;

const FeatureDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: #666;
  font-weight: 400;
  margin: 0;
  
  @media (max-width: 1200px) {
    font-size: 13px;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
  }
`;

